import { useEffect, useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Customer } from "../../../../../../../../models/customer";
import { Project } from "../../../../../../../../models/project";
import { Filters, Searches, Sort, Sorts } from "../../../../../../../../models/shipeezi.table";
import ProjectService from "../../../../../../../../services/project.service";
import { ProjectKeys } from "../../../../../../../../util/queries";

interface UseActionProjectProps {
  customer?: Customer;
}

export const useActionProject = ({ customer }: UseActionProjectProps) => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [projectList, setProjectList] = useState<Project[]>([]);
  const [project, setProject] = useState<Project>();
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();
  const CACHE_TIME = 3 * 60 * 60 * 1000; // 3 hour

  const handleChangeProject = (key: keyof Project, value: any) => {
    setProject((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const {
    data,
    refetch,
    isLoading: isLoadingProjects,
  } = useQuery({
    queryKey: ProjectKeys.getAndCountAll([
      page,
      pageSize,
      filters,
      searches,
      sorting,
      customer?.id,
    ]),
    queryFn: () =>
      ProjectService.getAndCountAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        groupBy: JSON.stringify(["projects.id"]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...(customer ? { customer_id: customer?.id } : {}),
        ...filters,
      }),
    enabled: !!currentUser?.id && !!currentUser.companyId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: CACHE_TIME,
  });

  interface CreateProjectProps {
    projectToCreate?: Project;
  }

  const { mutate: createProject, isLoading: isCreating } = useMutation({
    mutationKey: ProjectKeys.create(),
    mutationFn: ({ projectToCreate }: CreateProjectProps) =>
      ProjectService.create({
        ...(projectToCreate ?? project),
        companyId: currentUser?.companyId,
        ...(customer ? { customerId: customer?.id, customerName: customer.fullName } : {}),
      }),
    onSuccess: () => {
      NotificationToast.success(translate("projectCreatedWithSuccess"));
      setHasErrors(false);
      refetch();
    },
  });

  interface UpdateProjectProps {
    projectToUpdate?: Project;
  }

  const { mutate: updateProject, isLoading: isUpdating } = useMutation({
    mutationKey: ProjectKeys.update(),
    mutationFn: ({ projectToUpdate }: UpdateProjectProps) =>
      ProjectService.update({
        ...(projectToUpdate ?? project),
      }),
    onSuccess: () => {
      NotificationToast.success(translate("projectUpdatedWithSuccess"));
      setHasErrors(false);
      refetch();
    },
  });

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  useEffect(() => {
    if (data?.data?.rows) {
      setProjectList(data?.data?.rows);
      setTotal(data?.data.count);
    }
  }, [data]);

  return {
    variables: {
      projectList,
      project,
      hasErrors,
      page,
      pageSize,
      total,
      isLoadingProjects,
      isCreating,
      isUpdating,
    },
    handleChangeProject,
    setPageSize,
    setProject,
    createProject,
    updateProject,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
  };
};
